import { ReactElement, useCallback } from 'react';
import { useResettableState } from 'hooks/useResettableState';
import { useLocation } from 'react-router-dom';
import { MemoInspectionForm } from 'components/inspection/Form';
import { WithInputForm } from 'components/dataCreationForms/withInputForm';
import { Spinner } from 'components/core';
import { getInspectionStoreState } from 'store/inspection';
import { InspectionStatus } from 'shared/domain/inspection/inspectionModel';
import { SUBMIT_EVENT_TYPE } from '../model';
import {
  createInspectionFormValidation,
  inspectionValidation,
  validateInspectionField,
} from '../validation';
import { InspectionWizardViewProps } from '../types';
import {
  isCreatePath,
  isInspectionTemplatePath,
} from 'setup/navigation/routeMatchers';

function getFormResetKey(
  pathname: string,
  locationKey: string | undefined,
  inspectionId: string | undefined,
  templateId: string | undefined
): string {
  const isCreateMode =
    isCreatePath(pathname) || isInspectionTemplatePath(pathname);

  if (!isCreateMode) {
    return inspectionId ?? 'new';
  }

  return `create-${templateId ?? 'new'}-${locationKey}`;
}

function resolveInitialInspectionStatus(
  isCompleted: boolean | undefined
): InspectionStatus {
  return isCompleted
    ? InspectionStatus.completed
    : InspectionStatus.inProgress;
}

export function InspectionWizardContent(
  props: InspectionWizardViewProps
): ReactElement {
  const {
    loading,
    template,
    inspection,
    initialValues,
    submitForm,
    isSaving,
    back,
    dispatchSubmit,
  } = props;

  const location = useLocation();
  const templateId = template?._id ?? inspection?.template?._id;

  // Starts over for another inspection or when its completion changes in the store.
  const [inspectionStatus, setInspectionStatus] = useResettableState(
    `${inspection?._id}:${inspection?.isCompleted}`,
    () => resolveInitialInspectionStatus(inspection?.isCompleted)
  );

  const formKey = getFormResetKey(
    location.pathname,
    location.key,
    inspection?._id,
    templateId
  );

  const getProtocolItemsForFinish = useCallback(
    () => getInspectionStoreState().inspectionModel?.protocol,
    []
  );

  const submitWithStatus = useCallback(
    (
      form: Parameters<typeof submitForm>[0],
      uploaders?: Parameters<typeof submitForm>[1],
      options?: Parameters<typeof submitForm>[2]
    ) =>
      submitForm(form, uploaders, {
        ...options,
        isCompleted: inspectionStatus === InspectionStatus.completed,
      }),
    [inspectionStatus, submitForm]
  );

  if (loading || !template) {
    return <Spinner reason='InspectionWizard loading' />;
  }

  // No WithExportToDocumentation: inspection wizard has no issue context (PT-7064).
  return (
    <WithInputForm
      key={formKey}
      values={initialValues}
      errors={{}}
      eventType={SUBMIT_EVENT_TYPE}
      handleFieldValidation={validateInspectionField}
      handleFormValidation={createInspectionFormValidation(
        inspectionValidation,
        template,
        getProtocolItemsForFinish
      )}
      onSubmit={submitWithStatus}
      dispatchSubmit={dispatchSubmit}
      formName='inspection'
    >
      <MemoInspectionForm
        loading={loading}
        isSaving={isSaving}
        onSubmit={dispatchSubmit}
        onCancel={back}
        inspectionStatus={inspectionStatus}
        onStatusChange={setInspectionStatus}
      />
    </WithInputForm>
  );
}
